import assign from 'lodash/assign.js';
import cloneDeep from 'lodash/cloneDeep.js';

import type {
  BasePermission,
  DebugEvent,
  EmptyObject,
  ErrorEvent,
  EventListener,
  InternalEvent,
  InternalTimingEvent,
  InternalValueEvent,
  LoggedEvent,
  UserInteractionEvent,
  WarningEvent,
} from './loggingTypes';
import { LoggingEventType } from './loggingTypes';

// In extreme circumstances we stop logging to avoid memory leaks.
const maxQueueSize = 1e3;

/**
 * An event bus for logging.
 *
 * Loggers are registered as event listeners. There's no way to unregister because the design
 * assumes that the logger is instantiated only once per application.
 *
 * Propagates permissions. Logging is disabled by default and needs to be enabled via
 * `logger.allow(TrackingPermission.ESSENTIAL)`
 *
 * Stores global context. When global context changes it is expected that that `logger.setContext({
 * newValue: 'foo', clearValue: undefined })` etc is called.
 *
 * Provides convenience methods like `logger.logInfo` that can be exported.
 *
 * See EventListener for explanation of the generics.
 */
export class LoggingEventBus<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvent extends EmptyObject = EmptyObject,
  Permission extends BasePermission = BasePermission
> {
  private readonly contextProviders = new Set<() => Partial<LoggingContext>>();
  private readonly eventListeners: EventListener<LoggingContext, CustomEvent, Permission>[] = [];
  private readonly permissions = new Set<keyof Permission>();
  protected readonly queuedEvents: Array<[LoggedEvent<CustomEvent>, Partial<LoggingContext>]> = [];

  constructor(...listeners: EventListener<LoggingContext, CustomEvent, Permission>[]) {
    this.eventListeners.push(...listeners);
  }

  /**
   * Adds event listeners.
   *
   * Sets the allowed permissions on them.
   *
   * Does not do anything fancy, so you can hypothetically register similar listeners multiple
   * times.
   */
  public addEventListeners(
    ...listeners: EventListener<LoggingContext, CustomEvent, Permission>[]
  ): void {
    this.eventListeners.push(...listeners);

    for (const listener of listeners) {
      for (const permission of this.permissions) {
        listener.allow(permission).catch(console.error);
      }
    }
  }

  /** Adds a context provider that gets called before an event is logged. */
  public addContextProvider = (provider: () => Partial<LoggingContext>): void => {
    this.contextProviders.add(provider);
  };

  /** Remove a context provider. */
  public removeContextProvider = (provider: () => Partial<LoggingContext>): void => {
    this.contextProviders.delete(provider);
  };

  /**
   * Sends the event to all of the event listeners.
   *
   * Ques up the event if no listeners are present. Ensures that the context is grabbed when the
   * event is queued.
   */
  public logEvent = (event: LoggedEvent<CustomEvent>, context?: Partial<LoggingContext>): void => {
    const combinedContext = this.getCombinedContext(context);

    // Queue events if we don't have any listeners.
    if (this.eventListeners.length === 0) {
      if (this.queuedEvents.length > maxQueueSize) {
        console.warn('Logging queue is full. Stopping logging.');
        return;
      }
      this.queuedEvents.push([event, combinedContext]);
      return;
    }

    this.notifyListeners(event, combinedContext);
  };

  /** Combines the event context with all the context providers. */
  protected getCombinedContext = (context?: Partial<LoggingContext>): Partial<LoggingContext> => {
    const combinedContext: Partial<LoggingContext> = {};

    for (const getContext of this.contextProviders) {
      // The context provider is external and can crash logging if not caught.
      try {
        assign(combinedContext, getContext());
      } catch (error) {
        console.error(`Unable to get context from ${getContext.name}`, error);
      }
    }

    // Ensure event context gets last values
    if (context) {
      assign(combinedContext, cloneDeep(context));
    }

    return combinedContext;
  };

  /**
   * Notifies all the listeners of the event.
   *
   * IMPORTANT: This is different from `logEvent` because it doesn't get the new context at the time
   * of the event record because it may be different (which is bad).
   */
  protected notifyListeners = (
    event: LoggedEvent<CustomEvent>,
    context: Partial<LoggingContext>
  ): void => {
    for (const listener of this.eventListeners) {
      listener.logEvent(event, context);
    }
  };

  /** Logs any events that have been queued up. */
  protected logQueuedEvents = (): void => {
    if (this.queuedEvents.length === 0) return;
    for (const [event, context] of this.queuedEvents) {
      this.notifyListeners(event, context);
    }
    this.queuedEvents.length = 0;
  };

  /**
   * Notifies listeners that a permission has been granted.
   *
   * NOTE: All listeners require 'logging' to be granted before they can log anything.
   *
   * These are primary for client-side logging that is blocked on users accepting a a performance
   * tracking cookie.
   */
  public allow = async (permission: keyof Permission): Promise<void> => {
    this.permissions.add(permission);
    await Promise.all(this.eventListeners.map(listener => listener.allow(permission)));
    this.logQueuedEvents();
  };

  /**
   * Notifies listeners that a permission has been given away.
   *
   * This typically does not happen except on the web where a user can mid-session turn off
   * tracking.
   */
  public deny = async (permission: keyof Permission): Promise<void> => {
    this.permissions.delete(permission);
    await Promise.all(this.eventListeners.map(listener => listener.deny(permission)));
  };

  /**
   * Flushes all of the listeners.
   *
   * Logs any errors to the console/stderr.
   */
  public flush = async (): Promise<void> => {
    const tryFlushListeners = this.eventListeners.map(listener => {
      // Need to handle any errors that may occur in a single listener so it doesn't block processing of subsequent listeners.
      return listener.flush().catch(error => console.error(error));
    });

    await Promise.allSettled(tryFlushListeners);
  };

  // ==========================================================================
  // Convenience methods.
  // ==========================================================================

  /**
   * Logs an error event.
   *
   * NOTE: This increments a counter where the key is `${data.component}.${data.action}`. The
   * message field is only written to text collectors notice.
   *
   * @param data - The event data for logging.
   * @param data.error - The error that was thrown. This is used by collectors like Sentry or
   *   GoogleCloud ErrorReporting for stack traces.
   * @param data.message - The error message that was throw to override the error message for
   *   clarity.
   * @param data.action - The action that was in progress when the error occurred.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logError = (data: Omit<ErrorEvent, 'type'>, context?: Partial<LoggingContext>): void => {
    this.logEvent({ ...data, type: LoggingEventType.ERROR }, context);
  };

  /**
   * Logs a warning event.
   *
   * NOTE: This increments a counter where the key is `${data.component}.${data.action}`. The
   * message field is only written to text collectors notice.
   *
   * @param data - The event data for logging.
   * @param data.message - The warning message that was is written to standard err.
   * @param data.action - The action that was in progress when the warning occurred.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logWarning = (
    data: Omit<WarningEvent, 'type'>,
    context?: Partial<LoggingContext>
  ): void => {
    this.logEvent({ ...data, type: LoggingEventType.WARNING }, context);
  };

  /**
   * Logs a debug event.
   *
   * NOTE: This does not increment a counter and has a freeform message. The message field is only
   * written to text collectors notice.
   *
   * @param data - The event data for logging.
   * @param data.message - The informational message that was is written to standard out.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logDebug = (data: Omit<DebugEvent, 'type'>, context?: Partial<LoggingContext>): void => {
    this.logEvent({ ...data, type: LoggingEventType.DEBUG }, context);
  };

  /**
   * Logs a structured event (no message field) with information.
   *
   * NOTE: This increments a counter where the key is
   * `${data.component}.${data.action}.${data.label}`. So structure your logs so they make sense.
   * I.e. `NewsletterForm.Submit.20250101` or `SyncJob.Finish.bulk_edit`. There is no `message`
   * field. Use `logDebug` if you need to print informational text.
   *
   * @param data - The event data for logging.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param data.action - The action that occurred.
   * @param data.label - Label distinguishing between different variants of the `component`. I.e. If
   *   you have multiple forms, this could be the form id, or if you have a generic endpoint, this
   *   would be the id of the request.
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logInfo = (data: Omit<InternalEvent, 'type'>, context?: Partial<LoggingContext>): void => {
    this.logEvent({ ...data, type: LoggingEventType.INFO }, context);
  };

  /**
   * Logs a structured event (no message field) with a timing value. Use this for incrementing gauge
   * metrics where the domain is time in milliseconds (ms).
   *
   * NOTE: This increments a gauge metric where the key is
   * `${data.component}.${data.action}.${data.label}`. So structure your logs so they make sense.
   * I.e. `Page.Load.loading_time_ms` or `SyncJob.Finish.duration_ms`.
   *
   * @param data - The event data for logging.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param data.action - The action that occurred.
   * @param data.label - Label distinguishing between different variants of the `component`. I.e. If
   *   you have multiple forms, this could be the form id, or if you have a generic endpoint, this
   *   would be the id of the request.
   * @param data.valueMs - The timing value in milliseconds.
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logTiming = (
    data: Omit<InternalTimingEvent, 'type'>,
    context?: Partial<LoggingContext>
  ): void => {
    this.logEvent({ ...data, type: LoggingEventType.TIMING }, context);
  };

  /**
   * Logs a structured event (no message field) with a numeric value. Use this for incrementing
   * gauge metrics where the domain is something other than time (use logTiming for those).
   *
   * NOTE: This increments a gauge metric where the key is
   * `${data.component}.${data.action}.${data.label}`. So structure your logs so they make sense.
   * I.e. `Page.Load.downloaded_bytes` or `SyncJob.Finish.memory_usage_kb`.
   *
   * @param data - The event data for logging.
   * @param data.component - The component that the event occurred in. Could be a UI component like
   *   MyForm, or an endpoint component like JobSubmitHandler
   * @param data.action - The action that occurred.
   * @param data.label - Label distinguishing between different variants of the `component`. I.e. If
   *   you have multiple forms, this could be the form id, or if you have a generic endpoint, this
   *   would be the id of the request.
   * @param data.value - The value. The record is unitless. You need to configure your graphing
   *   software to add the units
   * @param context - Optional partial logging context. The event listeners use this to extract
   *   metadata for logging.
   */
  public logValue = (
    data: Omit<InternalValueEvent, 'type'>,
    context?: Partial<LoggingContext>
  ): void => {
    this.logEvent({ ...data, type: LoggingEventType.VALUE }, context);
  };

  /**
   * Same as `logInfo` except that for some collectors a new flag indicating that the event is
   * user-intiated is set.
   */
  public logUserEvent = (
    data: Omit<UserInteractionEvent, 'type'>,
    context?: Partial<LoggingContext>
  ): void => {
    this.logEvent({ ...data, type: LoggingEventType.USER_ACTION }, context);
  };

  /**
   * Logs a custom event.
   *
   * These events do not go to any standard collector unless you provide a custom transformer.
   *
   * This is useful when you want to match a custome event table in Blizzard and don't want any
   * other collector to pick it up for privacy reasons.
   */
  public logCustom = (data: CustomEvent, context?: Partial<LoggingContext>): void => {
    this.logEvent({ ...data, type: LoggingEventType.CUSTOM }, context);
  };
}
